/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import axios from 'axios';
import { cloneDeep } from 'lodash-es';
import { defineStore } from 'pinia';

import { WorkloadDetailsApiService, WorkloadsApiService, CampaignAndTasksApiService } from '@/services/api';
import WorkloadModel from '@/model/workloads/workload.model';
import WorkloadVersionModel from '@/model/workloads/workload-version.model';
import ComposeWorkloadVersionModel from '@/model/workloads/compose-workload-version.model';

import WorkloadsHelper from '@/store/helpers/workloads.helper';

import RemoteConnectionModel from '@/model/node/remoteConnection.model';
import i18n from '@/i18n';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';
import { TYPE_OF_WORKLOADS } from '@/constants';

const { CancelToken } = axios;
let source = CancelToken.source();

const useWorkloadDetailsStore = defineStore('workloadDetails', {
  state: () => ({
    selectedWorkload: new WorkloadModel({}),
    selectedVersion: new WorkloadVersionModel({}),
    selectedWorkloadVersions: [],
    uploadedFile: {},
    uploadedXmlFile: {},
    progress: 0,
    wlBasicInfo: {
      name: '',
      description: '',
      internalDockerRegistry: true,
    },
    volumeNameList: [],
    selectedVolumeName: '',
    originalVersion: new WorkloadVersionModel({}),
    isSaveEnabled: false,
    formChangedFlag: false,
    oldWlBasicInfo: {
      name: '',
      description: '',
      internalDockerRegistry: true,
    },
    downloadCounter: {},
  }),
  actions: {
    disableSaveButton() {
      this.isSaveEnabled = false;
    },
    saveUploadedFile(file) {
      this.uploadedFile = file;
    },
    saveUploadedXmlFile(file) {
      this.uploadedXmlFile = file;
    },
    async deleteVersion(param) {
      if (param.type === 'docker-compose' || (param.type === 'docker' && param.isUsingInternalDockerRegistry)) {
        await WorkloadsApiService.deleteWorkloadVersionUsingV3Api(param);
      } else {
        await WorkloadsApiService.deleteVersion(param);
      }
      this.selectedWorkloadVersions.splice(
        this.selectedWorkloadVersions.findIndex((version) => version._id === param.verId),
        1,
      );
      const utilsStore = useUtilsStore();
      utilsStore.showCustomToast({
        text: 'workloadDetail.dialog.successfullyDeleted',
        color: 'success',
        showClose: true,
      });
    },
    setWorkload(wl) {
      this.selectedWorkload = new WorkloadModel(wl);
      this.selectedWorkloadVersions = this.selectedWorkload.versions;
    },
    async getWorkloadById(id) {
      if (id === 'new') {
        this.selectedWorkload = new WorkloadModel({
          name: this.selectedWorkload.name,
          description: this.selectedWorkload.description,
          type: this.selectedWorkload.type,
          ...(this.selectedWorkload.type &&
            this.selectedWorkload.type === 'docker' && {
              internalDockerRegistry: this.selectedWorkload.internalDockerRegistry,
            }),
        });
        this.selectedWorkloadVersions = [];
        return;
      }

      if (this.selectedWorkload.internalDockerRegistry && this.selectedWorkload.type === 'docker') {
        const wl = await WorkloadsApiService.getWlByIdV3({ id });
        const versions = await WorkloadsApiService.getVersionListUsingV3Api(id);
        wl.versions = versions.data;
        this.setWorkload(wl);
        return;
      }
      const wl = await WorkloadsApiService.getWlById({ id });
      this.setWorkload(wl);
    },

    /**
     * @description Get workload versions using v2 or v3 API based on the type of workload and whether it is using internal docker registry
     * this function is used in deploy form to get the versions of the selected workload
     * @param {object} selectedWorkload - the workload for which the versions are to be fetched
     * @returns {array} versions - the versions of the selected workload
     */
    async getWorkloadVersionsUsingSelectedWl(selectedWorkload) {
      const versions = [];
      // v3 API is used for all docker compose workloads and for docker wl using internal docker registry, for other workloads v2 API is used
      if (selectedWorkload.internalDockerRegistry || selectedWorkload.type === 'docker-compose') {
        const { data } = await WorkloadsApiService.getVersionListUsingV3Api(selectedWorkload._id);
        versions.push(...data.map((version) => new ComposeWorkloadVersionModel({ version })));
      } else {
        const wl = await WorkloadsApiService.getWlById({ id: selectedWorkload._id });
        versions.push(...wl.versions);
      }
      return versions;
    },

    async getComposeWorkloadById(id) {
      if (id === 'new') {
        this.selectedWorkload = new WorkloadModel({
          name: this.selectedWorkload.name,
          description: this.selectedWorkload.description,
          type: this.selectedWorkload.type,
          internalDockerRegistry: this.selectedWorkload.internalDockerRegistry,
        });
        this.selectedWorkloadVersions = [];
        return;
      }
      const wl = await WorkloadsApiService.getWorkloadUsingV3Api({ id });
      // TO_DO: Why are we setting versions here??? This should be in workload-versions store!
      const versions = await WorkloadsApiService.getVersionListUsingV3Api(id);
      wl.versions = versions.data;
      if (wl.versions.length) {
        wl.versions.map((version) => {
          this.downloadCounter[version._id] = 1;
        });
      }

      this.setWorkload(wl);
    },
    setVersion(params) {
      this.volumeNameList = [];
      this.selectedVolumeName = '';
      if (params.versionId === 'new') {
        this.selectedVersion = new WorkloadVersionModel({
          version: {},
          type: this.selectedWorkload.type,
          ...(this.selectedWorkload.type === 'docker' &&
            this.selectedWorkload.internalDockerRegistry && {
              internalDockerRegistry: this.selectedWorkload.internalDockerRegistry,
            }),
        });

        return;
      }
      const isDockerWlUsingInternalDockerRegistry =
        this.selectedWorkload.internalDockerRegistry && this.selectedWorkload.type === 'docker';
      this.selectedVersion = new WorkloadVersionModel({
        version:
          isDockerWlUsingInternalDockerRegistry && params.version
            ? params.version
            : this.selectedWorkload.versions.find((version) => version?._id === params.versionId),
        type: this.selectedWorkload.type,
        ...(this.selectedWorkload.type === 'docker' &&
          this.selectedWorkload.internalDockerRegistry && {
            internalDockerRegistry: this.selectedWorkload.internalDockerRegistry,
          }),
      });
      if (this.selectedVersion.workloadProperties && this.selectedVersion.workloadProperties.docker_volumes) {
        this.volumeNameList.push(i18n.global.t('workloadVersion.emptyState'));
        const seenVolumeNames = new Set();
        this.selectedVersion.workloadProperties.docker_volumes.forEach((volume) => {
          if (volume.volumeName && !seenVolumeNames.has(volume.volumeName)) {
            seenVolumeNames.add(volume.volumeName);
            this.volumeNameList.push(volume.volumeName);
          }
          if (volume.configurationStorage === true) {
            this.selectedVolumeName = volume.volumeName;
          }
        });
        return;
      }
      this.selectedVolumeName = '';
    },
    async getVersionById(params) {
      this.selectedWorkload.type = params.workloadType;
      if (!params.isUpdate) {
        const versions = this.selectedWorkloadVersions;
        if (versions.length && this.selectedWorkload._id) {
          if (params.id === 'new') {
            if (params.hasCreatePermission) {
              versions[versions.length - 1].remoteConnections.forEach((rc) => {
                delete rc.id;
                delete rc._id;
              });
            } else {
              versions[versions.length - 1].remoteConnections = [];
            }
          } else {
            const versionIndex = versions.findIndex((version) => version.id === params.id);
            if (params.hasCreatePermission) {
              versions[versionIndex].remoteConnections.forEach((rc) => {
                delete rc.id;
                delete rc._id;
              });
            } else {
              versions[versionIndex].remoteConnections = [];
            }
          }
          this.selectedWorkload.versions = versions;
        }
      }
      if (params.id === 'new' && this.selectedWorkload._id !== '') {
        const versions = this.selectedWorkloadVersions;
        if (versions.length) {
          this.setVersion({
            versionId: versions[versions.length - 1]._id,
            workloadId: this.selectedWorkload._id,
          });
          this.originalVersion = cloneDeep(this.selectedVersion);
          return;
        }
      }
      this.setVersion({ versionId: params.id, workloadId: this.selectedWorkload._id || 'new' });
      this.originalVersion = cloneDeep(this.selectedVersion);
    },
    setVersions({ versions, type, internalDockerRegistry }) {
      this.selectedWorkloadVersions = versions.map(
        (version) =>
          new WorkloadVersionModel({
            version,
            type,
            ...(type === 'docker' && internalDockerRegistry && { internalDockerRegistry }),
          }),
      );
    },
    async fetchVersion(param) {
      const workload = this.selectedWorkload;
      param.search = decodeURIComponent(param.search);
      const versions =
        param && param.search && param.search.length
          ? workload.versions.filter((v) => v.name.includes(param.search) && v.isDeployable)
          : workload.versions.filter((v) => v.isDeployable);
      this.setVersions({
        versions,
        type: workload.type,
        internalDockerRegistry: workload.internalDockerRegistry,
      });
    },
    async deploy(deployInfo) {
      const deploy = await CampaignAndTasksApiService.deployWorkload(deployInfo);
      return deploy.operation_name;
    },
    setDownloadState(payload) {
      this.selectedWorkloadVersions.map((v) => {
        if (v._id === payload.version || v._id === payload.versionId) {
          if (!this.downloadCounter[v._id]) {
            this.downloadCounter[v._id] = 1;
          }
          if (v.summarizedFileStatuses && v.summarizedFileStatuses.downloading !== this.downloadCounter[v._id]) {
            v.isDownloading = true;
            this.downloadCounter[v._id] += 1;
            return;
          }
          v.isDownloading = false;
          this.downloadCounter[v._id] = 1;
        }
      });
    },
    setVersionDeployableProp(payload) {
      this.selectedWorkloadVersions.map((v) => {
        if (v._id === payload.version || v._id === payload.versionId) {
          v.isDeployable = true;
        }
      });
    },
    async updateWorkloadUsingV3Api(payload) {
      const utilsStore = useUtilsStore();
      const { id, data } = payload;
      if (id && data) {
        utilsStore.showProgressBarDialog();
        await WorkloadsApiService.updateWorkloadUsingV3Api({ id, data });
        utilsStore.showCustomToast({
          text: 'workloadDetail.successfullyUpdate',
          color: 'green',
          showClose: true,
        });
      }
    },
    async updateWorkloadErrorMessages(payload) {
      if (payload.error) {
        this.selectedWorkloadVersions.map((v) => {
          if (v._id === payload.version || v._id === payload.versionId) {
            v.errorMessage = payload.error;
            if (payload.error === 'nerve_workload_file_download_failed') {
              v.isFailed = true;
            }
            if (!this.downloadCounter[v._id]) {
              this.downloadCounter[v._id] = 1;
            }
            if (
              payload.error !== 'nerve_workload_files_cancelled' &&
              v.summarizedFileStatuses &&
              v.summarizedFileStatuses.downloading !== this.downloadCounter[v._id]
            ) {
              v.isDownloading = true;
              this.downloadCounter[v._id] += 1;
              return;
            }
            v.isDownloading = false;
            this.downloadCounter[v._id] = 1;
          }
        });
      }
    },
    setSaveEnabled(currentVersion) {
      this.isSaveEnabled = WorkloadsHelper.shouldSaveBtnBeEnabled({
        currentVersion,
        originalVersion: this.originalVersion,
      });
    },
    async updateVersion(payload) {
      source = CancelToken.source();
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      await WorkloadsApiService.updateVersion({
        payload,
        token: source.token,
        cb: (progress) => this.setProgress(progress),
      });
    },
    setProgress(progress) {
      this.progress = progress;
    },
    cancelRequest() {
      source.cancel();
    },
    async createWorkload(payload) {
      source = CancelToken.source();
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      const workload = await WorkloadsApiService.createNewWorkloadVersion({
        payload,
        token: source.token,
        cb: (progress) => this.setProgress(progress),
      });
      this.setWorkload(workload);
    },
    async createWorkloadUsingV3Api(payload) {
      source = CancelToken.source();
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      const wl = await WorkloadsApiService.createWorkloadUsingV3Api({
        payload,
        token: source.token,
        cb: (progress) => this.setProgress(progress),
      });
      this.setWorkload(wl);
      return wl._id;
    },
    uploadFile(param) {
      return WorkloadsApiService.addFile(param);
    },
    changeFile(param) {
      return WorkloadsApiService.editFile(param);
    },
    setWlBasicInfo(value) {
      this.wlBasicInfo.name = value.name;
      this.wlBasicInfo.description = value.desc;
      this.wlBasicInfo.internalDockerRegistry = value.internalDockerRegistry;
    },
    setOldWlBasicInfo(value) {
      this.oldWlBasicInfo.name = value.name;
      this.oldWlBasicInfo.description = value.desc;
      this.oldWlBasicInfo.internalDockerRegistry = value.internalDockerRegistry;
    },
    removeRCFromVersion(params) {
      this.selectedVersion.remoteConnections.splice(params.index, 1);
    },
    removeRc() {
      this.selectedVersion.remoteConnections = [];
    },
    addEditRcFromWorkload({ connection }) {
      // rc already exist -> edit the existing rc
      if (connection.index !== undefined) {
        this.selectedVersion.remoteConnections.splice(connection.index, 1, new RemoteConnectionModel(connection));
        return;
      }
      this.selectedVersion.remoteConnections.push(new RemoteConnectionModel(connection));
    },
    formHasChanged() {
      this.formChangedFlag = !this.formChangedFlag;
    },
    async getUniqueInfo(params) {
      return await WorkloadsApiService.getUniqueInfo(params);
    },
    async cancelComposeVersionCreation({ wlId, verId }) {
      await WorkloadsApiService.cancelComposeWorkloadVersionCreation({ wlId, verId });
    },
    async setComposeVersionStatus(payload) {
      const utilsStore = useUtilsStore();
      const { sessionId } = JSON.parse(localStorage.getItem('session'));
      if (payload.status === 'nerve_workload_file_download_failed') {
        this.updateWorkloadErrorMessages({
          error: payload.status,
          version: payload.versionId,
        });
        // only user that initiated action (create workload) will receive a messages
        if (sessionId === payload.sessionId) {
          utilsStore.showCustomToast({
            text: payload.errorCode
              ? `errorMessages.${payload.errorCode}`
              : 'errorMessages.WORKLOAD_DOCKER_IMG_DOWNLOAD_ERROR',
            color: 'red',
            showClose: true,
          });
        }
        return;
      }
      if (payload.status === 'nerve_workload_files_cancelled') {
        this.updateWorkloadErrorMessages({
          error: payload.status,
          version: payload.versionId,
        });
        return;
      }
      if (payload.status === 'nerve_workload_file_deleted') {
        this.updateWorkloadErrorMessages({
          error: payload.status,
          version: payload.versionId,
        });
      }
      if (payload.status === 'nerve_workload_file_downloaded') {
        // only user that initiated action (create workload) will receive a messages
        if (sessionId === payload.sessionId) {
          utilsStore.showCustomToast({
            text: 'errorMessages.WORKLOAD_DOCKER_IMG_DOWNLOADED_SUCCESSFULLY',
            color: 'green',
            showClose: true,
          });
        }
        this.setDownloadState(payload);
        this.setVersionDeployableProp(payload);
      }
    },
    setSelectedWorkloadInternalDockerRegistry(value) {
      this.selectedWorkload.internalDockerRegistry = value;
    },
    updateAffectedVersionsStatus(workloads) {
      workloads?.forEach((workload) => {
        workload.versions?.forEach((version) => {
          this.selectedWorkloadVersions.map((v) => {
            if (v._id === version.versionId) {
              v.isDeployable = version.isVersionDeployable;
            }
            return v;
          });
        });
      });
    },

    async setExportVersionStatus({ workloadId, versionId, exportObj, showNotification }) {
      const utilsStore = useUtilsStore();
      if (showNotification) {
        utilsStore.showCustomToast({
          text: 'errorMessages.EXPORT_READY_FOR_DOWNLOAD',
          color: 'green',
          showClose: true,
        });
      }
      if (this.selectedWorkload._id === workloadId) {
        this.selectedWorkloadVersions.map((version) => {
          if (version._id === versionId) {
            version.export = exportObj;
          }
          return version;
        });
      }
    },

    handleDockerRegistryEvent(payload) {
      const { name, params } = payload;
      switch (name) {
        case 'image_pushed_to_registry':
          this.updateAffectedVersionsStatus(params.workloads);
          break;
        case 'image_deleted_from_registry':
          this.updateAffectedVersionsStatus(params.workloads);
          break;
      }
    },

    async handleWorkloadEvents(message) {
      const sessionId = localStorage.getItem('session')
        ? JSON.parse(localStorage.getItem('session'))?.sessionId
        : undefined;

      const name = message?.name;
      const messageParams = message?.params || {};
      const utilsStore = useUtilsStore();

      const isInitiator = !!sessionId && sessionId === messageParams.sessionId;

      const showToast = (text, color) =>
        utilsStore.showCustomToast({
          text,
          color,
          showClose: true,
        });

      // Export: update export status, then exit
      if (name === 'workload_version_export') {
        await this.setExportVersionStatus({
          workloadId: messageParams.workload,
          versionId: messageParams.version,
          exportObj: {
            status: messageParams.export?.status,
            archiveName: messageParams.export?.archiveName,
            startTime: messageParams.export?.startTime,
          },
          showNotification: true,
        });
        return;
      }

      // Compose: always notify + update status, then exit
      if (messageParams.type === TYPE_OF_WORKLOADS.COMPOSE) {
        await this.setComposeVersionStatus(messageParams);
        return;
      }

      // Only the initiating user gets these toasts
      if (isInitiator) {
        const isSuccess = !!messageParams.success;

        if (!isSuccess) {
          if (messageParams.type === TYPE_OF_WORKLOADS.VM) {
            showToast('errorMessages.WORKLOAD_VM_IMG_CONVERSION_ERROR', 'red');
          } else if (
            messageParams.type === TYPE_OF_WORKLOADS.DOCKER &&
            messageParams.error !== 'nerve_workload_cancelled'
          ) {
            showToast(
              messageParams.errorCode
                ? `errorMessages.${messageParams.errorCode}`
                : 'errorMessages.WORKLOAD_DOCKER_IMG_DOWNLOAD_ERROR',
              'red',
            );
          }
        } else if (messageParams.type === TYPE_OF_WORKLOADS.VM) {
          showToast('errorMessages.WORKLOAD_VM_IMG_CONVERTED_SUCCESSFULLY', 'green');
        }
      }
      // Update store state
      if (messageParams.error) {
        await this.updateWorkloadErrorMessages(messageParams);
      } else if (isInitiator) {
        if (messageParams.type === 'docker') {
          const utilsStore = useUtilsStore();
          utilsStore.showCustomToast({
            text: 'errorMessages.WORKLOAD_DOCKER_IMG_DOWNLOADED_SUCCESSFULLY',
            color: 'green',
            showClose: true,
          });
        }
        this.setDownloadState(messageParams);
        this.setVersionDeployableProp(messageParams);
      }

      const eventMessage = messageParams?.params?.message;
      if (messageParams.code !== 1 || !eventMessage) return;

      if (isInitiator) {
        showToast(
          eventMessage === 'WORKLOAD_BACKUP_IS_IN_PROGRESS_AND_THEREFORE_WORKLOAD_CANNOT_BE_STARTED'
            ? 'errorMessages.VM_BACKUP_IS_IN_PROGRESS_CANNOT_BE_STARTED'
            : eventMessage,
          'red',
        );
      }
    },
    async updateWorkload(data) {
      const utilsStore = useUtilsStore();
      utilsStore.showProgressBarDialog();
      await WorkloadDetailsApiService.addUpdateWorkload(data);
      utilsStore.showCustomToast({
        text: 'workloadDetail.successfullyUpdate',
        color: 'success',
        showClose: true,
      });
    },
    updateDownloadCounter(versionId) {
      Object.keys(this.downloadCounter).map((key) => (this.downloadCounter[key] = 1));
      this.downloadCounter[versionId] = 1;
    },
    discardWorkloadChanges() {
      Object.assign(this.selectedWorkload, this.oldWlBasicInfo);
    },
  },
});

export default useWorkloadDetailsStore;
